import React from 'react';

import PropTypes from 'prop-types';
import { FiEdit } from 'react-icons/fi';
import { HiOutlinePrinter, HiOutlineTrash } from 'react-icons/hi';
import { omit } from 'lodash';
import { JsonParam, useQueryParam, withDefault } from 'use-query-params';

import { Button } from 'modules/common/input';
import GenericTable from 'modules/common/components/GenericTable';
import EmptyState from 'modules/common/components/EmptyState';
import Pagination from 'modules/common/components/Pagination';
import {
  useGetMenuBuilderList,
  useRemoveMenuBuilder,
} from 'modules/merchant/settings/menu-builder/hooks';
import MenuBuilderModal from 'modules/merchant/settings/menu-builder/modals/MenuBuilderModal';
import { removeEmpty, print } from 'modules/common/utils';
import { showAlert, showModal } from 'modules/common/modal';
import { REMOVE_SELECTED_MENU_BUILDER } from 'modules/merchant/settings/menu-builder/constants';

import { DEFAULT_FILTER } from './Filter';

const renderContent = () => (
  <div className="text-gray-600">
    <h4 className="font-medium text-lg text-black">Delete this QR Code</h4>
    <p className="text-sm my-4 leading-6 tracking-wide">
      Deleting this code will remove its information from our database. This
      cannot be undone!
    </p>
  </div>
);

const Print = ({ data }) => (
  <div
    style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center',
      width: '350px',
    }}
  >
    <div
      style={{
        textTransform: 'capitalize',
        fontSize: '30px',
        fontWeight: '900',
      }}
    >
      {data?.branch_uuid?.label}
    </div>
    <div
      style={{
        textTransform: 'uppercase',
        fontSize: '20px',
        color: 'red',
        fontWeight: '900',
        marginTop: '5px',
        marginBottom: '10px',
      }}
    >
      {data?.name}
    </div>
    <img src={data?.value} alt="qr-code" width="100%" />
  </div>
);

Print.defaultProps = {
  data: {},
};

Print.propTypes = {
  data: PropTypes.instanceOf(Object),
};

function TableQrCodes({ branch_uuid }) {
  const [query, setQuery] = useQueryParam(
    'table_qr_codes_query',
    withDefault(JsonParam, DEFAULT_FILTER)
  );

  const [isListLoading, list, pagination] = useGetMenuBuilderList(
    removeEmpty(
      omit({ ...query, branch_uuid: branch_uuid?.value }, ['selected'])
    )
  );
  const [isRemoveApiLoading, remove] = useRemoveMenuBuilder();

  const handleOnCreateUpdate = (row) => () => {
    showModal({
      focusTrap: false,
      title: `${row ? 'Update' : 'Add new'} Table`,
      modalSize: 'modal-xs',
      content: (onClose) =>
        row ? (
          <MenuBuilderModal
            data={row}
            branch={branch_uuid}
            type="UPDATE"
            onClose={onClose}
          />
        ) : (
          <MenuBuilderModal branch={branch_uuid} onClose={onClose} />
        ),
    });
  };
  const handleOnPrint = (row) => () => {
    print(`print-${row?.uuid}`);
  };
  const handleOnRemove = (row) => (e) => {
    e.preventDefault();
    showAlert({
      content: () => renderContent(),
      onYes: (onClose) => {
        remove(row?.uuid, () => {
          onClose();
        });
      },
      onYesLabel: 'Delete',
      onYesClassName: 'btn primary md w-40 rounded',
      onNoLabel: 'Cancel',
      onNoClassName: 'btn outline-light md w-40 rounded',
      buttonContainerClassName: 'flex justify-end gap-2 mt-4',
      isLoadingKey: REMOVE_SELECTED_MENU_BUILDER,
      isLoadingMethod: 'remove',
    });
  };
  const handleOnChangePage = (page) => {
    setQuery({
      ...query,
      page,
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="font-semibold">Table QR Codes</div>
          <div className="text-sm text-gray-500"> </div>
        </div>
        <div>
          <Button
            label="Add new Table"
            className="btn primary font-semibold !px-3 sm"
            onClick={handleOnCreateUpdate()}
          />
        </div>
      </div>

      <GenericTable
        isLoading={isListLoading}
        id="table-qr-codes-table"
        withHover
        noRowsLabel={<EmptyState />}
        containerClassName="min-h-[300px] max-h-[300px]"
        data={list}
        format={[
          {
            key: 'name',
            label: 'TABLE NAME',
            widthClassName: 'flex-1',
          },
          !branch_uuid && {
            key: (item) => item?.branch_uuid?.label,
            label: 'Branch',
          },
          {
            key: (item) => (
              <div className="flex gap-3.5 items-center">
                <div id={`print-${item?.uuid}`} className="hidden">
                  <Print data={item} />
                </div>

                <Button
                  label={
                    <FiEdit className="h-[18px] w-[18px] cursor-pointer" />
                  }
                  onClick={handleOnCreateUpdate(item)}
                  disabled={isRemoveApiLoading}
                />
                <Button
                  label={
                    <HiOutlinePrinter className="h-5 w-5 cursor-pointer" />
                  }
                  onClick={handleOnPrint(item)}
                  disabled={isRemoveApiLoading}
                />
                <Button
                  label={
                    <HiOutlineTrash className="h-5 w-5 text-primary-400 cursor-pointer" />
                  }
                  onClick={handleOnRemove(item)}
                  disabled={isRemoveApiLoading}
                />
              </div>
            ),
            label: 'ACTIONS',
            widthClassName: 'flex-1',
          },
        ]}
      />
      <Pagination
        value={pagination}
        onChange={handleOnChangePage}
        small
        isLoading={isListLoading}
      />
    </div>
  );
}

TableQrCodes.defaultProps = {
  branch_uuid: 'BRANCH',
};

TableQrCodes.propTypes = {
  branch_uuid: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
};

export default TableQrCodes;
